import { ChangeEvent, JSX } from 'react';
import {
    Box,
    IconButton,
    InputAdornment,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { EmailFinder } from '../../../../../../types/entities';
import { formatDateTime, shortUrl } from '../email-finder-format';
import { StatusChip } from './StatusChip';

interface ScanListProps {
    finders: EmailFinder[];
    loading: boolean;
    error: string;
    total: number;
    page: number;
    pageSize: number;
    searchTerm: string;
    selectedId: string | undefined;
    onSearch: (term: string) => void;
    onPage: (page: number) => void;
    onSelect: (finder: EmailFinder) => void;
    onRefresh: () => void;
}

/**
 * The scan picker. Each entry used to be a five row key and value stack about
 * 275px tall, so two of eighty-nine scans were on screen at once and finding
 * one meant paging. A row is now three lines: what it is called and how it
 * ended, then the counts, then where it pointed.
 */
export const EmailFinderScanList: (props: ScanListProps) => JSX.Element =
    (props: ScanListProps): JSX.Element => {
        const totalPages: number = Math.max(1, Math.ceil(props.total / props.pageSize));
        const firstRow: number = props.total === 0 ? 0 : ((props.page - 1) * props.pageSize) + 1;
        const lastRow: number = Math.min(props.page * props.pageSize, props.total);

        return (
            <Box sx={ { display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 } }>
                <Box sx={ { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' } }>
                    <TextField value={ props.searchTerm }
                               size="small"
                               type="search"
                               placeholder="Search scans"
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void => props.onSearch(e.target.value) }
                               slotProps={ {
                                   input: {
                                       startAdornment: (
                                           <InputAdornment position="start">
                                               <SearchIcon sx={ { fontSize: 18, color: 'text.secondary' } } />
                                           </InputAdornment>
                                       ),
                                   },
                               } }
                               sx={ { flex: 1 } } />
                    <Tooltip title="Reload the list">
                        <IconButton onClick={ props.onRefresh } size="small">
                            <RefreshIcon sx={ { fontSize: 20 } } />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={ {
                    flex: 1,
                    minHeight: '340px',
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                } }>
                    { props.loading &&
                        [0, 1, 2, 3, 4].map((row: number): JSX.Element => (
                            <Box key={ 'email_finder_skeleton_' + row }
                                 sx={ {
                                     padding: '12px 14px',
                                     borderBottom: '1px solid',
                                     borderBottomColor: 'divider',
                                 } }>
                                <Skeleton width="60%" height={ 18 } />
                                <Skeleton width="40%" height={ 14 } />
                                <Skeleton width="80%" height={ 14 } />
                            </Box>
                        ))
                    }

                    { !props.loading && props.error !== '' &&
                        <Box sx={ { padding: '24px 16px', textAlign: 'center', color: 'error.main' } }>
                            <Typography sx={ { fontSize: '13px' } }>{ props.error }</Typography>
                        </Box>
                    }

                    { !props.loading && props.error === '' && props.finders?.length === 0 &&
                        <Box sx={ { padding: '32px 16px', textAlign: 'center' } }>
                            <Typography sx={ { fontSize: '13px', color: 'text.secondary' } }>
                                { props.searchTerm === ''
                                    ? 'No scans yet. Start one above.'
                                    : `Nothing matches "${props.searchTerm}".` }
                            </Typography>
                        </Box>
                    }

                    { !props.loading && props.error === '' &&
                        props.finders?.map((item: EmailFinder): JSX.Element => {
                            const selected: boolean = String(props.selectedId) === String(item.id);

                            return (
                                <Box key={ 'email_finder_list_item_' + item.id }
                                     role="button"
                                     tabIndex={ 0 }
                                     onClick={ (): void => props.onSelect(item) }
                                     onKeyDown={ (e: { key: string }): void => {
                                         if (e.key === 'Enter' || e.key === ' ') {
                                             props.onSelect(item);
                                         }
                                     } }
                                     sx={ {
                                         padding: '11px 14px 11px 11px',
                                         borderBottom: '1px solid',
                                         borderBottomColor: 'divider',
                                         borderLeft: '3px solid',
                                         borderLeftColor: selected ? 'primary.main' : 'transparent',
                                         background: selected ? 'var(--wf-surface-2)' : 'transparent',
                                         cursor: 'pointer',
                                         outline: 'none',
                                         '&:hover': { background: 'var(--wf-surface-2)' },
                                         '&:focus-visible': { borderLeftColor: 'primary.main' },
                                     } }>
                                    <Box sx={ {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px',
                                    } }>
                                        <Typography title={ item.name }
                                                    sx={ {
                                                        fontSize: '13.5px',
                                                        fontWeight: 600,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    } }>
                                            { item.name }
                                        </Typography>
                                        <StatusChip status={ item.status } dense />
                                    </Box>

                                    <Typography sx={ {
                                        fontSize: '11.5px',
                                        color: 'text.secondary',
                                        marginTop: '3px',
                                    } }>
                                        { `#${item.id} · ${formatDateTime(item.createdAt)} · `
                                            + `${item.totalFound ?? 0} found, ${item.totalNew ?? 0} new` }
                                    </Typography>

                                    { item.targetUrl !== undefined &&
                                        <Typography title={ item.targetUrl }
                                                    sx={ {
                                                        fontSize: '11.5px',
                                                        fontFamily: 'var(--wf-font-mono)',
                                                        color: 'text.secondary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        marginTop: '2px',
                                                    } }>
                                            { shortUrl(item.targetUrl) }
                                        </Typography>
                                    }
                                </Box>
                            );
                        })
                    }
                </Box>

                <Box sx={ {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '8px',
                } }>
                    <IconButton size="small"
                                aria-label="Previous page"
                                disabled={ props.page <= 1 || props.loading }
                                onClick={ (): void => props.onPage(props.page - 1) }>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography sx={ { fontSize: '11.5px', color: 'text.secondary' } }>
                        { props.total === 0
                            ? 'No scans'
                            : `${firstRow}-${lastRow} of ${props.total}` }
                    </Typography>
                    <IconButton size="small"
                                aria-label="Next page"
                                disabled={ props.page >= totalPages || props.loading }
                                onClick={ (): void => props.onPage(props.page + 1) }>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Box>
        );
    }
